// Vendor Dashboard — MRR/ARR + risk listesi
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui';

const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const STATUS_COLORS = {
  active:        { bg: 'rgba(16,185,129,0.15)', clr: 'var(--success)' },
  trial:         { bg: 'rgba(59,130,246,0.15)',  clr: 'var(--info)' },
  trial_expired: { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  past_due:      { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  suspended:     { bg: 'rgba(239,68,68,0.15)',   clr: 'var(--danger)' },
  cancelled:     { bg: 'rgba(100,116,139,0.15)', clr: '#64748b' },
};
const STATUS_LABELS = {
  active: 'Aktif', trial: 'Trial', trial_expired: 'Trial Bitti',
  past_due: 'Vade Geçmiş', suspended: 'Askıya Alındı', cancelled: 'İptal',
};

export default function VendorDashboardPage({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/vendor/dashboard');
      if (r.data.success) setData(r.data.data);
      else setError(r.data.message || 'Veri alınamadı');
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || 'Bağlantı hatası');
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 16, color: 'var(--danger)' }}>
        <strong>Dashboard yüklenemedi:</strong> {error}
      </div>
      <div style={{ marginTop: 12 }}><Button size="sm" onClick={load}>Tekrar Dene</Button></div>
    </div>
  );
  if (!data) return null;
  const s = data.stats;

  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Vendor Dashboard</h2>
        </div>
        <div className="stocks-topbar-right">
          <Button size="sm" variant="ghost" onClick={load}>↻ Yenile</Button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {/* KPI kartları */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi label="MRR (Aylık Düzenli Gelir)" value={`₺${fmt(s.mrr)}`} accent="var(--success)" />
          <Kpi label="ARR (Yıllık)" value={`₺${fmt(s.arr)}`} accent="var(--accent-primary)" />
          <Kpi label="Aktif Firma" value={s.active_count} sub={`${s.total_companies} toplam`} />
          <Kpi label="Trial" value={s.trial_count} sub={`${s.trial_expired || 0} bitti`} />
          <Kpi label="Vade Geçmiş Tutar" value={`₺${fmt(s.overdue_amount)}`} sub={`${s.overdue_count} fatura`} accent="var(--danger)" />
          <Kpi label="Askıya Alınan / İptal" value={`${s.suspended_count} / ${s.cancelled_count}`} accent="#64748b" />
        </div>

        {/* Risk listesi */}
        <div style={{
          background: 'var(--bg-primary, #fff)',
          border: '1px solid var(--border-color, #e5e5e5)',
          borderRadius: 8, padding: 14, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14, flex: 1 }}>🔴 Risk Listesi (yakın aksiyon gerekli)</h3>
            {onNavigate && <Button size="sm" variant="ghost" onClick={() => onNavigate('vendor-companies')}>Tüm Firmalar →</Button>}
          </div>
          {data.risks.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>Risk altında firma yok 👍</div>
          )}
          {data.risks.length > 0 && (
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Kod</th><th>Firma</th><th>Plan</th><th>Durum</th>
                  <th style={{ textAlign: 'right' }}>Kalan Gün</th>
                </tr>
              </thead>
              <tbody>
                {data.risks.map(r => {
                  const c = STATUS_COLORS[r.subscription_status] || {};
                  return (
                    <tr key={r.id} className="stocks-table-row" style={{ cursor: onNavigate ? 'pointer' : 'default' }}
                        onClick={() => onNavigate && onNavigate('vendor-companies', { highlightId: r.id })}>
                      <td><span className="stock-code">{r.code}</span></td>
                      <td><strong>{r.name}</strong></td>
                      <td><span className="stock-secondary">{r.plan_name || '-'}</span></td>
                      <td>
                        <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, fontWeight: 700, background: c.bg, color: c.clr }}>
                          {STATUS_LABELS[r.subscription_status] || r.subscription_status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, color: r.days_left < 0 ? 'var(--danger)' : r.days_left <= 3 ? '#f59e0b' : 'var(--text-primary)' }}>
                          {r.days_left < 0 ? `${-r.days_left} gün geçti` : r.days_left === 0 ? 'Bugün' : `${r.days_left} gün`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--bg-primary, #fff)',
      border: '1px solid var(--border-color, #e5e5e5)',
      borderTop: accent ? `3px solid ${accent}` : '3px solid var(--border-color)',
      borderRadius: 8, padding: '14px 16px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: accent || 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
